import { useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import Close from '@mui/icons-material/Close';
import { Dialog, DialogContent, DialogContentProps, DialogProps, IconButton, Typography } from '@mui/material';

import { yupResolver } from '@hookform/resolvers/yup';

import { ROUTES } from '@arrived/common';
import { Auth0SignupAccountCreationEventProperties } from '@arrived/components';
import { signupSchema, SignupSchema } from '@arrived/forms';

import { NextMuiLink } from '../Link';
import { SignupForm, SignupFormProps } from './SignupForm';

export interface SignupDialogProps extends Omit<DialogProps, 'onClose'> {
  accountCreationEventProperties?: Auth0SignupAccountCreationEventProperties;
  close: () => void;
  DialogContentProps?: DialogContentProps;
  SignupFormProps?: Omit<SignupFormProps, 'useFormReturn'>;
}

/**
 * The SignupDialog has an e-mail form and alternative login option built in and can be customized with header content
 * and an additional CTA in the content above the e-mail form.
 */
export const SignupDialog = ({
  accountCreationEventProperties,
  children,
  close,
  DialogContentProps,
  SignupFormProps,
  sx,
  ...rest
}: SignupDialogProps) => {
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  const useFormReturn = useForm<SignupSchema>({
    mode: 'onChange',
    resolver: yupResolver(signupSchema),
  });

  const handleOnClose = useCallback(() => close(), [close]);

  const { children: DialogContentChildren, sx: DialogContentSx, ...DialogContentRest } = DialogContentProps || {};
  const { EmailFormTextFieldProps, LoadingButtonProps, ...SignupFormRest } = SignupFormProps || {};
  const { InputProps, ...EmailFormTextFieldRest } = EmailFormTextFieldProps || {};

  return (
    <Dialog
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: { xs: '480px', md: '680px' },
          px: { xs: 2, md: 8 },
          py: { xs: 6, md: 12 },
          position: 'relative',
        },
        ...sx,
      }}
      onClose={handleOnClose}
      {...rest}
    >
      <IconButton
        onClick={handleOnClose}
        sx={{
          position: 'absolute',
          top: ({ spacing }) => ({ xs: spacing(1), md: spacing(1.5) }),
          right: ({ spacing }) => ({ xs: spacing(1), md: spacing(1.5) }),
        }}
      >
        <Close />
      </IconButton>
      {children}
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', ...DialogContentSx }}
        {...DialogContentRest}
      >
        {DialogContentChildren}
        <SignupForm
          accountCreationEventProperties={accountCreationEventProperties}
          useFormReturn={useFormReturn}
          LoadingButtonProps={{
            children: <FormattedMessage id="next" defaultMessage="Next" />,
            sx: { mt: 2 },
            ...LoadingButtonProps,
          }}
          EmailFormTextFieldProps={{
            InputProps: { inputRef: emailInputRef, ...InputProps },
            ...EmailFormTextFieldRest,
          }}
          width="100%"
          gap={2}
          pt={4}
          pb={{ xs: 4, md: 7 }}
          {...SignupFormRest}
        />
        <Typography variant="body1">
          <FormattedMessage id="already-have-an-account" defaultMessage="Already have an account?" />
        </Typography>
        <NextMuiLink href={ROUTES.logIn}>
          <FormattedMessage id="log-in-here" defaultMessage="Log in here" />
        </NextMuiLink>
      </DialogContent>
    </Dialog>
  );
};
